import { FC, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

import { graphqlEndpoint } from 'AppSettings'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import { InfoField } from 'types/infoField'
import {
  getCourseOrderItemByCourseNumberHelper,
  getActiveDiscInstUrlInfo,
  setTitle
} from 'reducers/activecourse/activecourseSlice'
import {
  CourseMethodType,
  CourseOrderItem,
  CourseStatus,
  CourseTabTitle,
  getCourseStatusName
} from 'share/domain/course'
import { coursesDateFormat } from 'share/utils/dates'
import { CoursesContainer } from 'components/atoms/Layout'
import InfoFields from 'components/atoms/InfoFields'
import { downloadBlob } from 'share/utils/fileUtils'

import { ActionsContainer } from './styles'

interface CourseTabCompleteProps {
  course: CourseOrderItem
  currentTab: string
}

const certificateExpirationDays = 180

const CourseTabComplete: FC<CourseTabCompleteProps> = ({
  course,
  currentTab
}) => {
  const dispatch = useAppDispatch()
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const discInstUrlInfo = useAppSelector(getActiveDiscInstUrlInfo)
  const authentication = useAppSelector((state) => state.authentication)

  useEffect(() => {
    let title = ''

    if (activeCourse.activeTab) {
      title =
        activeCourse.activeTab === 'first'
          ? CourseTabTitle.FINISHED_FIRST_COURSE
          : CourseTabTitle.FINISHED_SECOND_COURSE
    }

    dispatch(setTitle(title))
  }, [activeCourse.activeTab])

  const downloadCertificate = () => {
    const BackendURL = graphqlEndpoint.replace('/graphql', '')
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Authorization', `Bearer ${authentication.idToken}`)
    requestHeaders.set('Access_Token', `${authentication.accessToken}`)

    const options = {
      method: 'GET',
      headers: requestHeaders
    }

    const courseOrderItem = getCourseOrderItemByCourseNumberHelper(
      activeCourse,
      activeCourse.activeTab
    )

    fetch(
      `${BackendURL}api/certificate/download/${courseOrderItem?.orderId}/${courseOrderItem?.orderItemId}`,
      options
    )
      .then((res) => res.blob())
      .then((blob) => {
        downloadBlob(blob, 'Certificate.pdf')
      })
      .catch((err) => console.log(err))
  }

  const getNeedToKnowTextByCurrentTab = () => {
    if (currentTab === 'first') {
      return (
        <>
          <p>
            Your course certificate has been sent to your attorney. Once your
            attorney files your case, you must complete the second course.
          </p>
          <p>
            Have you submitted all the{' '}
            <Link to='/dashboard/documents'>requested documents</Link>?
          </p>
        </>
      )
    } else {
      return <p>Your course certificate has been sent to your attorney.</p>
    }
  }

  const fields = useMemo<InfoField[]>(() => {
    const completedWithManualCert =
      course.courseStatus === CourseStatus.CompleteCertificateManuallyUploaded

    const courseUrl = discInstUrlInfo?.courseUrl.startsWith('https')
      ? discInstUrlInfo.courseUrl
      : `https://${discInstUrlInfo?.courseUrl}`

    const actions = (
      <ActionsContainer>
        <a href='#' onClick={downloadCertificate}>
          View Certificate
        </a>
        {!completedWithManualCert &&
          course.courseMethod === CourseMethodType.Online && (
            <a href={courseUrl} rel='noopener noreferrer' target='_blank'>
              Review Course
            </a>
          )}
      </ActionsContainer>
    )

    const needToKnow = getNeedToKnowTextByCurrentTab()

    const completionDate = coursesDateFormat(
      course.courseCompletionDate as string
    )

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const certificateExpiration = new Date(course.courseCompletionDate!)
    certificateExpiration.setDate(
      certificateExpiration.getDate() + certificateExpirationDays
    )
    const certificateDate = coursesDateFormat(certificateExpiration.toString())

    const result = [
      {
        content: getCourseStatusName(course.courseStatus),
        key: 'courseStatus',
        title: 'Course Status'
      },
      {
        content: completionDate,
        key: 'courseCompletionDate',
        title: 'Course Completion Date'
      },
      {
        content: certificateDate,
        key: 'certificateExpirationDate',
        title: 'Certificate Expiration Date'
      },
      {
        content: actions,
        key: 'actions',
        title: 'Actions'
      },
      {
        content: course.providerName,
        key: 'courseProvider',
        title: 'Course Provider'
      },
      {
        content: needToKnow,
        key: 'needToKnow',
        title: 'What you need to know:'
      }
    ]

    if (completedWithManualCert) {
      result.splice(4, 1)
      result.splice(1, 2)
    }

    return result
  }, [
    course.courseCertificationExpirationDate,
    course.courseCompletionDate,
    course.courseMethod,
    course.courseStatus,
    course.providerName,
    discInstUrlInfo?.courseUrl
  ])

  return (
    <CoursesContainer data-testid='course-tab-complete-content'>
      <InfoFields fields={fields} />
    </CoursesContainer>
  )
}

export default CourseTabComplete
